'use client';

/**
 * Canvas2Card — card-in-card container.
 *
 * Mounts other cards by content_key per a YAML-authored placement
 * list. Validates the recursion primitive (a card whose content is
 * a list of cards) without requiring the IPE shell refactor.
 *
 * Pattern is portable: works inside the IPE today (mounted by
 * mode7's preset), and will work inside a thin standalone web app
 * tomorrow with the same primitives — a BFF client + a card
 * registry. Nothing here depends on IPE-specific shell assumptions
 * beyond the capability lookup pattern (which any consumer of the
 * Klyde substrate has).
 *
 * Layout: v1 stacks placements vertically by `y` order, every card
 * full-width. The x/w/h fields are honoured in the schema but
 * ignored by this renderer — when a real grid layout is needed
 * (e.g., a two-column section inside a page), upgrade this to a
 * proper CSS-grid or react-grid-layout implementation. Same data
 * shape, richer renderer.
 *
 * @see klyde_edge/klyde_web_content/schemas.py — PageContainerContent
 * @see klyde_edge/klyde_web_content/data/web_landing.yaml — slug=web_landing_container
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import type { WebPageContainerContent, WebContainerPlacement } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';
import { CANVAS_CARD_REGISTRY } from '../card-registry';
import { CanvasCardShell } from '../canvas-card-shell';
import { CardErrorBoundary } from '../card-error-boundary';


interface Canvas2CardProps {
  /** Capability action to fetch the placements list. Different containers
   *  (landing, /for-buyers, future pages) each have their own strategy
   *  but share this renderer — same recursion shape, different content. */
  strategyAction?: string;
}

export function Canvas2Card({
  strategyAction = 'web_landing_container',
}: Canvas2CardProps = {}) {
  const T = useCardTypography();
  const containerStrategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebPageContainerContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerStrategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebPageContainerContent>(containerStrategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load page container');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [containerStrategy, lang]);

  // Sort placements by y so authoring order in YAML drives render order.
  // Tie-break by x so a row of two-column sections renders
  // left-to-right naturally.
  const ordered = useMemo<WebContainerPlacement[]>(() => {
    if (!data) return [];
    return [...data.placements].sort((a, b) =>
      a.y !== b.y ? a.y - b.y : a.x - b.x,
    );
  }, [data, lang]);

  // Detect whether the container actually wants a multi-column layout.
  // When ALL placements span the full 12 columns, stacking is correct
  // (existing landing / for-buyers / for-suppliers / resources). When
  // any placement is narrower, switch to CSS grid so authored x/w/h
  // coords drive the layout. Substrate doctrine: layout is data on the
  // placement, not bespoke per-page chrome.
  const useGrid = useMemo(
    () => ordered.some((p) => (p.w ?? 12) < 12),
    [ordered],
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive p-8">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading page…
      </div>
    );
  }

  // Take ownership of vertical scrolling so the container works
  // regardless of what wraps it. IPE canvas slot is fixed-height with
  // overflow:hidden by default. Children render in document flow
  // inside this scroll viewport. Z-index 0 + isolate establishes a
  // fresh stacking context so per-section z-index decisions don't
  // collide with IPE chrome above us.
  return (
    <div className="flex flex-col w-full h-full overflow-y-auto overflow-x-hidden isolate relative z-0">
      {useGrid ? (
        <div
          className="grid gap-6 px-6 py-8"
          style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))' }}
        >
          {ordered.map((placement, i) => {
            const Component = CANVAS_CARD_REGISTRY[placement.content_key];
            const w = Math.max(1, Math.min(12, placement.w ?? 12));
            const x = Math.max(0, Math.min(11, placement.x ?? 0));
            const h = Math.max(1, placement.h ?? 1);
            const cellStyle = {
              gridColumn: `${x + 1} / span ${w}`,
              gridRow:    `span ${h}`,
            } as React.CSSProperties;

            if (!Component) {
              return (
                <div
                  key={`${placement.content_key}-${i}`}
                  style={cellStyle}
                >
                  <UnknownCardSlot contentKey={placement.content_key} />
                </div>
              );
            }
            return (
              <CanvasCardShell
                key={`${placement.content_key}-${i}`}
                contentKey={placement.content_key}
                style={cellStyle}
              >
                <CardErrorBoundary contentKey={placement.content_key}>
                  <CardChromeProvider contentKey={placement.content_key}>
                    <Component />
                  </CardChromeProvider>
                </CardErrorBoundary>
              </CanvasCardShell>
            );
          })}
        </div>
      ) : (
        ordered.map((placement, i) => {
          const Component = CANVAS_CARD_REGISTRY[placement.content_key];
          if (!Component) {
            return (
              <UnknownCardSlot
                key={`${placement.content_key}-${i}`}
                contentKey={placement.content_key}
              />
            );
          }
          return (
            <CanvasCardShell
              key={`${placement.content_key}-${i}`}
              contentKey={placement.content_key}
              className="w-full"
            >
              <CardErrorBoundary contentKey={placement.content_key}>
                <CardChromeProvider contentKey={placement.content_key}>
                  <Component />
                </CardChromeProvider>
              </CardErrorBoundary>
            </CanvasCardShell>
          );
        })
      )}
    </div>
  );
}


/**
 * Placement references a content_key the registry doesn't know about.
 * Surfaces honestly rather than silently rendering nothing — usually
 * means the registry hasn't picked up a new card or there's a typo
 * in the YAML.
 */
function UnknownCardSlot({ contentKey }: { contentKey: string }) {
  return (
    <div className="border border-dashed border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
      Unknown card content_key: <code className="font-mono">{contentKey}</code>
      <div className="text-xs text-muted-foreground mt-1">
        Add an entry to <code>CANVAS_CARD_REGISTRY</code> or fix the placement in
        <code> web_landing.yaml</code>.
      </div>
    </div>
  );
}
